import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { api } from '../../api';
import type { RoleMember, RolePermission } from '../../api/areas/admin';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { STATUS_TONE } from './StaffPage';
import { RecordRef, useAdminCrumbs, useCatalogue } from './ui';

/** `/admin/roles/:code`: everything the role allows, grouped, and everyone who holds it. */
export function RolePage() {
  const { t } = useTranslation('admin');
  const { code = '' } = useParams();
  const result = useApi((signal) => api.getRole(code, { signal }), [code]);
  const { roleName, permissionName } = useCatalogue();
  const title = result.status === 'success' ? roleName(code, result.data.role.description) : code;
  useDocumentTitle(title || t('roles.title'));
  const crumbs = [...useAdminCrumbs(), { label: t('roles.title'), to: '/admin/roles' }, { label: title || '…' }];

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('roles.title')} crumbs={crumbs} />
        <Loading rows={6} />
      </>
    );
  }
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('roles.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('role.loadError')} />
      </>
    );
  }

  const { role, permissions, members } = result.data;
  const groups = [...new Set(permissions.map((p) => p.group))];

  const permissionColumns: Column<RolePermission>[] = [
    {
      key: 'what',
      header: t('role.permissions'),
      // Table cells do not wrap by default; some permission descriptions are a full sentence.
      // The floor goes on the content: the table is `w-full`, so a cell width is dropped on a
      // narrow screen and the sentence collapses to one word per line.
      className: 'whitespace-normal',
      cell: (p) => (
        <span className="block min-w-72">
          <span className="font-medium">{permissionName(p.code, p.description)}</span>
          <span className="block text-xs font-normal text-muted-foreground">{p.code}</span>
        </span>
      ),
    },
    {
      key: 'guards',
      header: t('role.alsoNeeds'),
      cell: (p) => (
        <div className="flex flex-wrap gap-1">
          {p.sensitive ? (
            <Badge tone="warn" title={t('role.needsMfaHint')}>
              {t('role.needsMfa')}
            </Badge>
          ) : null}
          {p.requires_four_eyes ? (
            <Badge tone="serious" title={t('role.needsTwoHint')}>
              {t('role.needsTwo')}
            </Badge>
          ) : null}
          {!p.sensitive && !p.requires_four_eyes ? <span className="text-muted-foreground">—</span> : null}
        </div>
      ),
    },
  ];

  const memberColumns: Column<RoleMember>[] = [
    {
      key: 'person',
      header: t('role.memberCol.person'),
      cell: (m) =>
        m.user_kind === 'STAFF' ? (
          <EntityRef label={m.display_name} sub={m.email} to={`/admin/staff/${m.user_id}`} id={m.user_id} />
        ) : (
          <EntityRef label={m.display_name} sub={m.email} id={m.user_id} />
        ),
    },
    { key: 'kind', header: t('role.memberCol.kind'), cell: (m) => t(`userKind.${m.user_kind}`) },
    {
      key: 'scope',
      header: t('role.memberCol.scope'),
      cell: (m) =>
        m.scope_type === 'GLOBAL' ? (
          t('scope.global')
        ) : (
          <RecordRef resourceType="customer" resourceId={m.scope_id} resourceName={m.scope_name} />
        ),
    },
    { key: 'state', header: t('role.memberCol.state'), cell: (m) => <Badge tone={STATUS_TONE[m.status]}>{t(`staffStatus.${m.status}`)}</Badge> },
    { key: 'granted', header: t('role.memberCol.granted'), cell: (m) => <span title={formatDateTime(m.granted_at)}>{formatRelative(m.granted_at)}</span> },
  ];

  return (
    <>
      <PageHeader
        title={title}
        crumbs={crumbs}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {role.code}
            {role.built_in ? (
              <Badge tone="neutral" title={t('roles.builtInHint')}>
                {t('roles.builtIn')}
              </Badge>
            ) : null}
          </span>
        }
      />
      <div className="space-y-4">
        {role.four_eyes_permission_count > 0 ? (
          <Callout tone="serious" title={t('note.fourEyesTitle')}>
            {t('note.fourEyesBody')}
          </Callout>
        ) : null}
        <Section title={t('role.about')}>
          <KeyValue
            columns={3}
            rows={[
              [t('role.code'), role.code],
              [t('role.description'), title],
              [t('roles.col.permissions'), t('roles.permissionCount', { count: role.permission_count })],
              [t('role.needsMfa'), role.sensitive_permission_count],
              [t('role.needsTwo'), role.four_eyes_permission_count],
              [t('role.members'), t('roles.memberCount', { count: role.member_count })],
            ]}
          />
        </Section>

        {permissions.length === 0 ? (
          <Section title={t('role.permissions')}>
            <Callout tone="warn">{t('role.noPermissions')}</Callout>
          </Section>
        ) : (
          groups.map((g) => (
            <Section
              key={g}
              flush
              title={t(`role.group.${g}` as 'role.group.domain', { defaultValue: g })}
              description={groups[0] === g ? t('role.permissionsHint') : undefined}
            >
              <DataTable columns={permissionColumns} rows={permissions.filter((p) => p.group === g)} rowKey={(p) => p.code} />
            </Section>
          ))
        )}

        <Section flush title={t('role.members')} description={t('role.membersHint')}>
          {members.length === 0 ? (
            <div className="px-5 pb-5">
              <EmptyState title={t('role.noMembers')} />
            </div>
          ) : (
            <DataTable columns={memberColumns} rows={members} rowKey={(m) => `${m.user_id}/${m.scope_type}/${m.scope_id ?? ''}`} />
          )}
        </Section>
      </div>
    </>
  );
}
